import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import KpiCard from "@/components/KpiCard";
import CompanyLogo from "@/components/CompanyLogo";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { UploadSimple, Warning, CheckCircle } from "@phosphor-icons/react";

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString();

export default function Company() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        // resolve slug -> id via companies list
        api.get("/companies").then(async (r) => {
            const company = r.data.find((c) => c.slug === slug);
            if (!company) { setErr("Company not found"); setLoading(false); return; }
            try {
                const res = await api.get(`/dashboard/company/${company.id}`);
                if (mounted) setData(res.data);
            } catch (e) {
                setErr(e.response?.data?.detail || e.message);
            } finally {
                if (mounted) setLoading(false);
            }
        });
        return () => { mounted = false; };
    }, [slug]);

    if (loading) return <div className="p-10 overline" data-testid="company-loading">Loading…</div>;
    if (err) return <div className="p-10 text-neg" data-testid="company-error">{String(err)}</div>;
    if (!data) return null;

    const { company, kpis, trend, recent_reports, recent_issues, recent_activities } = data;

    return (
        <div className="p-8 lg:p-10 max-w-[1400px]" data-testid={`company-page-${company.slug}`}>
            <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
                <div className="flex gap-5 items-start">
                    <CompanyLogo company={company} size="lg" />
                    <div>
                        <div className="overline mb-2">{company.sector}</div>
                        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter leading-none">{company.name}</h1>
                        <p className="text-sm text-muted2 mt-2 max-w-lg">{company.tagline}</p>
                    </div>
                </div>
                <Link to={`/upload?company=${company.id}`} className="btn-primary gap-2" data-testid="upload-for-company">
                    <UploadSimple size={16} /> Upload report
                </Link>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <KpiCard testid="company-kpi-revenue" label="Revenue" value={fmtUSD(kpis.revenue)} tone="pos" />
                <KpiCard testid="company-kpi-expenses" label="Expenses" value={fmtUSD(kpis.expenses)} tone="neg" />
                <KpiCard testid="company-kpi-net" label="Net" value={fmtUSD(kpis.net)} tone={kpis.net >= 0 ? "pos" : "neg"} />
                <KpiCard testid="company-kpi-reports" label="Reports filed" value={fmtNum(kpis.report_count)} sub={`${fmtNum(kpis.issues_count)} issues`} />
            </div>

            <div className="grid-card p-6 mb-6">
                <div className="overline mb-3">Financial & production trend</div>
                {trend.length === 0 ? (
                    <div className="h-64 flex items-center justify-center text-sm text-muted2">No data yet for this unit.</div>
                ) : (
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={trend}>
                                <CartesianGrid stroke="#E4E4E7" strokeDasharray="3 3" />
                                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#52525B" }} />
                                <YAxis tick={{ fontSize: 11, fill: "#52525B" }} />
                                <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #0A0A0A", fontSize: 12 }} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Area type="monotone" dataKey="revenue" stroke="#16A34A" fill="#16A34A22" strokeWidth={2} />
                                <Area type="monotone" dataKey="expenses" stroke="#DC2626" fill="#DC262622" strokeWidth={2} />
                                <Area type="monotone" dataKey="production" stroke="#002FA7" fill="#002FA722" strokeWidth={2} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <div className="grid-card p-6 lg:col-span-2">
                    <div className="overline mb-3">Recent reports</div>
                    <div className="divide-y divide-rule">
                        {recent_reports.length === 0 ? (
                            <div className="text-sm text-muted2 py-6">No reports yet for this company.</div>
                        ) : (
                            recent_reports.map((r) => (
                                <Link to={`/reports/${r.id}`} key={r.id} data-testid={`company-report-${r.id}`}
                                    className="py-3 flex items-start gap-3 hover:bg-paper transition-colors block">
                                    <div className="overline w-20 shrink-0 pt-1">{r.report_type === "financial" ? "Finance" : "Ops"}</div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium truncate">{r.extracted?.headline || r.summary || "(no headline)"}</div>
                                        <div className="text-xs text-muted2 truncate">{r.summary}</div>
                                    </div>
                                    <div className="text-xs text-muted2 data-num shrink-0">{r.report_date}</div>
                                </Link>
                            ))
                        )}
                    </div>
                </div>
                <div className="grid-card p-6">
                    <div className="overline mb-3">Open issues</div>
                    <div className="space-y-2">
                        {recent_issues.length === 0 ? (
                            <div className="text-sm text-muted2">No issues flagged.</div>
                        ) : (
                            recent_issues.map((i, idx) => (
                                <div key={idx} className="flex gap-2 text-sm">
                                    <Warning size={16} className="text-warn shrink-0 mt-0.5" />
                                    <div className="min-w-0">
                                        <div className="text-ink">{i.text}</div>
                                        <div className="text-xs text-muted2 data-num">{i.date}</div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                    <div className="overline mt-6 mb-3">Recent activities</div>
                    <div className="space-y-2">
                        {recent_activities.length === 0 ? (
                            <div className="text-sm text-muted2">No activities captured yet.</div>
                        ) : (
                            recent_activities.slice(0, 6).map((a, idx) => (
                                <div key={idx} className="flex gap-2 text-sm">
                                    <CheckCircle size={16} className="text-pos shrink-0 mt-0.5" />
                                    <div className="text-ink">{a.text}</div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
